import { type BaseMessage, KillSource, MessageType, StaticObjectType } from "./types";

export interface PingMessage extends BaseMessage {
  type: MessageType.Ping;
}

export interface PongMessage extends BaseMessage {
  type: MessageType.Pong;
}

export interface EngineControlMessage extends BaseMessage {
  type: MessageType.EngineControl;
  engineOn: boolean;
}

export interface ShootMessage extends BaseMessage {
  type: MessageType.Shoot;
}

export interface PitchControlMessage extends BaseMessage {
  type: MessageType.PitchControl;
  pitchPosition: number;
}

export interface UserConnectedMessage extends BaseMessage {
  type: MessageType.UserConnected;
  userId: string;
  color?: string;
}

export interface PlayerJoinedMessage extends BaseMessage {
  type: MessageType.PlayerJoined;
  playerId: string;
  color: string;
  x: number;
  y: number;
  facingDirection: number;
  hp: number;
  kills: number;
  lives: number;
}

export interface PlayerLeftMessage extends BaseMessage {
  type: MessageType.PlayerLeft;
  playerId: string;
}

export interface AirplaneState {
  playerId: string;
  x: number;
  y: number;
  angle: number;
  velocityX: number;
  velocityY: number;
  engineOn: boolean;
  pitchPosition: number;
  facingDirection: number;
  hp: number;
}

export interface BulletState {
  bulletId: string;
  ownerId: string;
  x: number;
  y: number;
  angle: number;
}

export interface GameStateMessage extends BaseMessage {
  type: MessageType.GameState;
  airplanes: AirplaneState[];
  bullets: BulletState[];
  timestamp: number;
}

export interface StaticObject {
  id: string;
  objectType: StaticObjectType;
  x: number;
  y: number;
  halfWidth: number;
  halfHeight: number;
}

export interface StaticObjectsMessage extends BaseMessage {
  type: MessageType.StaticObjects;
  objects: StaticObject[];
}

export interface WorldConfigMessage extends BaseMessage {
  type: MessageType.WorldConfig;
  worldScale: number;
  worldWidth: number;
}

export interface PlayerDeathMessage extends BaseMessage {
  type: MessageType.PlayerDeath;
  playerId: string;
  cause: StaticObjectType;
  respawnTime: number;
  livesRemaining: number;
}

export interface PlayerRespawnMessage extends BaseMessage {
  type: MessageType.PlayerRespawn;
  playerId: string;
  x: number;
  y: number;
  facingDirection: number;
  engineOn: boolean;
  lives: number;
}

export interface PlayerGameOverMessage extends BaseMessage {
  type: MessageType.PlayerGameOver;
  playerId: string;
}

export interface PlayerDamageMessage extends BaseMessage {
  type: MessageType.PlayerDamage;
  playerId: string;
  newHp: number;
  attackerId: string;
}

export interface PlayerKillMessage extends BaseMessage {
  type: MessageType.PlayerKill;
  killerId: string;
  victimId: string;
  source: KillSource;
  killerTotalKills: number;
}

export interface PlayerStats {
  playerId: string;
  color: string;
  kills: number;
  deaths: number;
}

export interface GameEndMessage extends BaseMessage {
  type: MessageType.GameEnd;
  leaderboard: PlayerStats[];
  restartTime: number;
}

export interface GameRestartMessage extends BaseMessage {
  type: MessageType.GameRestart;
}

export type ClientMessage =
  | PingMessage
  | EngineControlMessage
  | PitchControlMessage
  | ShootMessage;

export type ServerMessage =
  | PongMessage
  | UserConnectedMessage
  | PlayerJoinedMessage
  | PlayerLeftMessage
  | GameStateMessage
  | StaticObjectsMessage
  | WorldConfigMessage
  | PlayerDeathMessage
  | PlayerRespawnMessage
  | PlayerDamageMessage
  | PlayerKillMessage
  | PlayerGameOverMessage
  | GameEndMessage
  | GameRestartMessage;

export function createPingMessage(): PingMessage {
  return { type: MessageType.Ping };
}

export function createPongMessage(): PongMessage {
  return { type: MessageType.Pong };
}

export function createEngineControlMessage(
  engineOn: boolean,
): EngineControlMessage {
  return { type: MessageType.EngineControl, engineOn };
}

export function createShootMessage(): ShootMessage {
  return { type: MessageType.Shoot };
}

export function createPitchControlMessage(
  pitchPosition: number,
): PitchControlMessage {
  return { type: MessageType.PitchControl, pitchPosition };
}

export function createUserConnectedMessage(
  userId: string,
  color?: string,
): UserConnectedMessage {
  return { type: MessageType.UserConnected, userId, color };
}

export function createPlayerJoinedMessage(
  playerId: string,
  color: string,
  x: number,
  y: number,
  facingDirection: number,
  hp: number,
  kills: number,
  lives: number,
): PlayerJoinedMessage {
  return { type: MessageType.PlayerJoined, playerId, color, x, y, facingDirection, hp, kills, lives };
}

export function createPlayerLeftMessage(playerId: string): PlayerLeftMessage {
  return { type: MessageType.PlayerLeft, playerId };
}

export function createGameStateMessage(
  airplanes: AirplaneState[],
  bullets: BulletState[],
  timestamp: number,
): GameStateMessage {
  return { type: MessageType.GameState, airplanes, bullets, timestamp };
}

export function createStaticObjectsMessage(
  objects: StaticObject[],
): StaticObjectsMessage {
  return { type: MessageType.StaticObjects, objects };
}

export function createPlayerDeathMessage(
  playerId: string,
  cause: StaticObjectType,
  respawnTime: number,
  livesRemaining: number,
): PlayerDeathMessage {
  return { type: MessageType.PlayerDeath, playerId, cause, respawnTime, livesRemaining };
}

export function createPlayerRespawnMessage(
  playerId: string,
  x: number,
  y: number,
  facingDirection: number,
  engineOn: boolean,
  lives: number,
): PlayerRespawnMessage {
  return { type: MessageType.PlayerRespawn, playerId, x, y, facingDirection, engineOn, lives };
}

export function createPlayerGameOverMessage(playerId: string): PlayerGameOverMessage {
  return { type: MessageType.PlayerGameOver, playerId };
}

export function createPlayerDamageMessage(
  playerId: string,
  newHp: number,
  attackerId: string,
): PlayerDamageMessage {
  return { type: MessageType.PlayerDamage, playerId, newHp, attackerId };
}

export function createPlayerKillMessage(
  killerId: string,
  victimId: string,
  source: KillSource,
  killerTotalKills: number,
): PlayerKillMessage {
  return { type: MessageType.PlayerKill, killerId, victimId, source, killerTotalKills };
}

export function createWorldConfigMessage(
  worldScale: number,
  worldWidth: number,
): WorldConfigMessage {
  return { type: MessageType.WorldConfig, worldScale, worldWidth };
}

export function createGameEndMessage(leaderboard: PlayerStats[], restartTime: number): GameEndMessage {
  return { type: MessageType.GameEnd, leaderboard, restartTime };
}

export function createGameRestartMessage(): GameRestartMessage {
  return { type: MessageType.GameRestart };
}
